import { supabase } from '@/integrations/supabase/client';
import type { TrafficLightColor, ClientWithTrafficLight } from '@/components/admin/ClientTrafficLightTable';

interface ClientSummary {
  id: string;
  name: string;
  email: string;
  created_at: string;
  is_active: boolean;
  suspended_until: string | null;
  access_expires_at: string | null;
  suspension_reason: string | null;
  subscription_tier: 'free' | 'premium';
  subscription_expires_at: string | null;
  stripe_subscription_id: string | null;
  latestSummary: {
    foco_score: number;
    total_income: number;
    savings_rate: number;
    debt_ratio: number;
    variable_expense_ratio: number;
    real_cash_flow: number;
    year: number;
    month: number;
  } | null;
  goalsCount: number;
  transactionsCount: number;
  lastTransactionDate: string | null;
}

export function calculateTrafficLight(client: ClientSummary): TrafficLightColor {
  const summary = client.latestSummary;
  
  // Calculate days since last activity
  const lastActivity = client.lastTransactionDate 
    ? new Date(client.lastTransactionDate) 
    : new Date(client.created_at);
  const daysSinceActivity = Math.floor((Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));

  // Red conditions
  if (daysSinceActivity >= 7) return 'red';
  if (summary) {
    if (summary.variable_expense_ratio > 0.4) return 'red';
    if (summary.savings_rate < 0.05) return 'red';
    if (summary.debt_ratio > 0.35) return 'red';
  }

  // Yellow conditions
  if (daysSinceActivity >= 4) return 'yellow';
  if (summary) {
    if (summary.variable_expense_ratio > 0.3) return 'yellow';
    if (summary.savings_rate < 0.1) return 'yellow';
    if (summary.debt_ratio > 0.2) return 'yellow';
  }

  return 'green';
}

export function calculateLastActivityDays(client: ClientSummary): number {
  const lastActivity = client.lastTransactionDate 
    ? new Date(client.lastTransactionDate) 
    : new Date(client.created_at);
  return Math.floor((Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));
}

export async function generateClientAlerts(client: ClientSummary): Promise<void> {
  const summary = client.latestSummary;
  const daysSinceActivity = calculateLastActivityDays(client);
  
  const alertsToCreate: Array<{
    user_id: string;
    alert_type: string;
    title: string;
    description: string;
    severity: string;
    suggested_action: string;
  }> = [];

  // Check for existing unresolved alerts to avoid duplicates
  const { data: existingAlerts } = await supabase
    .from('client_alerts')
    .select('alert_type')
    .eq('user_id', client.id)
    .eq('is_resolved', false);

  const existingTypes = new Set(existingAlerts?.map(a => a.alert_type) || []);

  // No activity for 7+ days
  if (daysSinceActivity >= 7 && !existingTypes.has('NO_ACTIVITY_7D')) {
    alertsToCreate.push({
      user_id: client.id,
      alert_type: 'NO_ACTIVITY_7D',
      title: 'Sin actividad en 7+ días',
      description: `El cliente no ha registrado transacciones en ${daysSinceActivity} días.`,
      severity: 'warning',
      suggested_action: 'Contactar al cliente para verificar su seguimiento financiero.',
    });
  }

  if (summary) {
    // Variable expenses > 40%
    if (summary.variable_expense_ratio > 0.4 && !existingTypes.has('VARIABLE_TOO_HIGH')) {
      alertsToCreate.push({
        user_id: client.id,
        alert_type: 'VARIABLE_TOO_HIGH',
        title: 'Gastos variables muy altos',
        description: `Los gastos variables representan ${(summary.variable_expense_ratio * 100).toFixed(0)}% de los ingresos.`,
        severity: 'critical',
        suggested_action: 'Revisar categorías de gasto variable y crear presupuestos por categoría.',
      });
    }

    // Savings < 5%
    if (summary.savings_rate < 0.05 && !existingTypes.has('SAVINGS_TOO_LOW')) {
      alertsToCreate.push({
        user_id: client.id,
        alert_type: 'SAVINGS_TOO_LOW',
        title: 'Tasa de ahorro crítica',
        description: `La tasa de ahorro es solo ${(summary.savings_rate * 100).toFixed(0)}%, muy por debajo del objetivo.`,
        severity: 'critical',
        suggested_action: 'Establecer meta de ahorro y reducir gastos variables.',
      });
    }

    // Debt ratio > 35%
    if (summary.debt_ratio > 0.35 && !existingTypes.has('DEBT_RATIO_HIGH')) {
      alertsToCreate.push({
        user_id: client.id,
        alert_type: 'DEBT_RATIO_HIGH',
        title: 'Endeudamiento alto',
        description: `El ratio de deuda es ${(summary.debt_ratio * 100).toFixed(0)}%, superando el límite recomendado.`,
        severity: 'critical',
        suggested_action: 'Implementar estrategia de bola de nieve para reducir deudas.',
      });
    }
  }

  // Insert new alerts
  if (alertsToCreate.length > 0) {
    await supabase.from('client_alerts').insert(alertsToCreate);
  }
}

export async function fetchClientsWithTrafficLights(): Promise<ClientWithTrafficLight[]> {
  // Fetch all profiles
  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false });

  if (profilesError) throw profilesError;

  // Fetch summaries for all users
  const { data: summaries } = await supabase
    .from('monthly_summaries')
    .select('*')
    .order('year', { ascending: false })
    .order('month', { ascending: false });

  // Fetch goals count per user
  const { data: goals } = await supabase
    .from('financial_goals')
    .select('user_id');

  // Fetch transactions with dates
  const { data: transactions } = await supabase
    .from('transactions')
    .select('user_id, date')
    .order('date', { ascending: false });

  // Fetch alert counts
  const { data: alerts } = await supabase
    .from('client_alerts')
    .select('user_id')
    .eq('is_resolved', false);

  // Build client data with traffic lights
  const clientsData: ClientWithTrafficLight[] = (profiles || []).map(profile => {
    const userSummaries = (summaries || []).filter(s => s.user_id === profile.id);
    const latestSummary = userSummaries.length > 0 ? userSummaries[0] : null;
    const userGoals = (goals || []).filter(g => g.user_id === profile.id);
    const userTransactions = (transactions || []).filter(t => t.user_id === profile.id);
    const userAlerts = (alerts || []).filter(a => a.user_id === profile.id);
    const lastTransactionDate = userTransactions.length > 0 ? userTransactions[0].date : null;

    const clientSummary: ClientSummary = {
      id: profile.id,
      name: profile.name,
      email: profile.email,
      created_at: profile.created_at,
      is_active: profile.is_active ?? true,
      suspended_until: profile.suspended_until,
      access_expires_at: profile.access_expires_at,
      suspension_reason: profile.suspension_reason,
      subscription_tier: profile.subscription_tier ?? 'free',
      subscription_expires_at: profile.subscription_expires_at,
      stripe_subscription_id: profile.stripe_subscription_id ?? null,
      latestSummary: latestSummary ? {
        foco_score: latestSummary.foco_score || 0,
        total_income: Number(latestSummary.total_income) || 0,
        savings_rate: Number(latestSummary.savings_rate) || 0,
        debt_ratio: Number(latestSummary.debt_ratio) || 0,
        variable_expense_ratio: Number(latestSummary.variable_expense_ratio) || 0,
        real_cash_flow: Number(latestSummary.real_cash_flow) || 0,
        year: latestSummary.year,
        month: latestSummary.month,
      } : null,
      goalsCount: userGoals.length,
      transactionsCount: userTransactions.length,
      lastTransactionDate,
    };

    return {
      ...clientSummary,
      lastActivityDays: calculateLastActivityDays(clientSummary),
      overallTrafficLight: calculateTrafficLight(clientSummary),
      alertsCount: userAlerts.length,
    };
  });

  return clientsData;
}
